import { Reservation, ReservationItem } from 'src/types/createBooking';
import { ISearchRoom, ReservationItemService } from 'src/types/common';
import { Cookies } from 'quasar';

const Attribution_KEY = 'hop_ga4';

export interface IConsentRecordInfo {
  date: Date;
  analytics: boolean;
  marketing: boolean;
}

export interface GA4CookieData {
  ga_client_id?: string;
  ga_session_id?: string;
  ga_session_number?: string;
}

export interface GA4AttributionData {
  gtmId?: string;
  utm_id?: string;
  utm_source_platform?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  wbraid?: string;
  gbraid?: string;
  ga_client_id?: string;
  ga_session_id?: string;
  timestamp: number;
}

type ConsentValue = 'granted' | 'denied';

interface ConsentSettings {
  ad_storage?: ConsentValue;
  ad_user_data?: ConsentValue;
  ad_personalization?: ConsentValue;
  analytics_storage?: ConsentValue;
  functionality_storage?: ConsentValue;
  personalization_storage?: ConsentValue;
  security_storage?: ConsentValue;
}

interface ConsentDefaultSettings extends ConsentSettings {
  /** ms to wait for an async CMP to call update before tags fire */
  wait_for_update?: number;
  /** ISO 3166-2 codes, e.g. ['GB', 'DE', 'US-CA'] */
  region?: string[];
}

interface Gtag {
  (command: 'consent', action: 'default', params: ConsentDefaultSettings): void;
  (command: 'consent', action: 'update', params: ConsentSettings): void;
  (command: 'set', field: 'ads_data_redaction' | 'url_passthrough', value: boolean): void;
  (command: 'set', params: Record<string, unknown>): void;
  (command: 'js', date: Date): void;
  (command: 'config', targetId: string, params?: Record<string, unknown>): void;
  (command: 'event', eventName: string, params?: Record<string, unknown>): void;
}

declare global {
  interface Window {
    gtag: Gtag;
  }
}

function removeAfterLastSlash(str: string): string {
  const lastSlashIndex = str.lastIndexOf('/');
  if (lastSlashIndex === -1) return str; // Return original string if no slash found
  return str.substring(0, lastSlashIndex);
}

const trackPageView = (propertyCode: string, page: string, event?: string) => {
  if (event === undefined || event === null || event === '') {
    if (page.indexOf('Rooms-And-Rates') > 0) {
      event = 'RoomsAndRates';
    } else if (page.indexOf('Checkout') > 0) {
      event = 'Checkout';
    } else if (page.indexOf('Confirmation') > 0) {
      event = 'Confirmation';
      page = removeAfterLastSlash(page); // remove the session Id
    } else {
      event = 'NewPageLoaded';
    }
  }

  const virtualPage = page;
  if (typeof window.dataLayer !== 'undefined') {
    window.dataLayer.push({
      VirtualPage: virtualPage,
      event: event,
      PropertyCode: propertyCode,
      dateTime: new Date(),
    });
  }

  if (window.fbq !== undefined) {
    window.fbq('track', 'ViewContent', { content_name: virtualPage });
  }
};

const trackPromotionView = (promoCode: string) => {
  if (typeof window.dataLayer !== 'undefined' && promoCode != null) {
    window.dataLayer.push({
      VirtualPage: '/booking-engine/PromotionCodeAdded',
      event: 'PromotionCodeAdded',
      dateTime: new Date(),
      ecommerce: {
        promoView: {
          promotions: [
            {
              id: promoCode,
              name: promoCode,
              //'creative': '',
              //'position': ''
            },
          ],
        },
      },
    });
  }

  if (window.fbq !== undefined) {
    window.fbq('trackCustom', 'EnterPromoCode', { promotion: promoCode });
  }
};

const trackBookingConfirmation = (
  propertyCode: string,
  currency: string,
  promoCode: string,
  confirmationNumber: string,
  totalCost: number,
  totalTaxes: number,
  reservation: Reservation,
  reservationItems: Array<ReservationItem>,
) => {
  if (typeof window.dataLayer !== 'undefined') {
    const attributionData = getAnalyticsData();

    const user_properties = clean({
      utm_id: attributionData?.utm_id,
      utm_source_platform: attributionData?.utm_source_platform,
      utm_source: attributionData?.utm_source,
      utm_medium: attributionData?.utm_medium,
      utm_campaign: attributionData?.utm_campaign,
      utm_term: attributionData?.utm_term,
      utm_content: attributionData?.utm_content,
      gclid: attributionData?.gclid,
      ga_client_id: attributionData?.ga_client_id,
      ga_session_id: attributionData?.ga_session_id,
    });

    const roomData = reservationItems.map((r: ReservationItem) => {
      return {
        sku: r.RoomTypeId,
        name: r.roomType.Name,
        category: 'Room Type: ' + r.roomType.Name,
        brand: 'Rate Plan: ' + r.ratePlan.RatePlanCode,
        variant: 'From Date: ' + r.DateOfArrival,
        ratePlanId: r.ratePlan.RatePlanId,
        ratePlanName: r.ratePlan.RatePlanCode,
        roomTypeId: r.roomType.RoomTypeId,
        roomTypeName: r.roomType.Name,
        price: r.totalCostForAnalytics,
        taxes: r.totalTaxesForAnalytics,
        netPrice: r.totalCostForAnalytics - r.totalTaxesForAnalytics,
        currency: currency,
        quantity: 1,
        dateFrom: r.DateOfArrival,
        dateTo: r.DateOfDeparture,
      };
    });
    const servicesData = reservationItems
      .flatMap((item) => item.Services)
      .map((s: ReservationItemService) => {
        return {
          sku: s.Service.ServiceCode,
          name: s.Service.ServiceName,
          category: 'Service',
          price: s.TotalCost,
          quantity: s.Quantity,
        };
      });

    const transactionProducts = [...roomData, ...servicesData];

    const bookingConfirmationData = {
      VirtualPage: propertyCode + '/booking-engine/booking-confirmation',
      event: 'BookingConfirmation',
      transactionId: confirmationNumber,
      transactionAffiliation: propertyCode,
      hotel_id: propertyCode,
      transactionTotal: totalCost,
      transactionTotalTaxes: totalTaxes,
      transactionTotalNet: totalCost - totalTaxes,
      transactionCurrency: currency,
      transactionProducts: transactionProducts,
      promotionCode: promoCode,
      dateTime: new Date(),
    };

    const confirmationEventData = {
      ...bookingConfirmationData,
      user_properties: user_properties,
    };

    window.dataLayer.push(confirmationEventData);

    const productData = reservationItems.map((r: ReservationItem) => {
      return {
        id:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        name:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        price: r.totalCostForAnalytics,
        taxes: r.totalTaxesForAnalytics,
        netPrice: r.totalCostForAnalytics - r.totalTaxesForAnalytics,
        ratePlanId: r.ratePlan.RatePlanId,
        ratePlanName: r.ratePlan.RatePlanCode,
        roomTypeId: r.roomType.RoomTypeId,
        roomTypeName: r.roomType.Name,
        quantity: 1,
        category: 'Room Type: ' + r.roomType.Name,
        brand: 'Rate Plan: ' + r.ratePlan.RatePlanCode,
        variant: 'From Date: ' + r.DateOfArrival,
        metric1: r.DateOfArrival,
        metric2: r.DateOfDeparture,
      };
    });

    const products = [...productData, ...servicesData];

    // For enhanced e-commerce
    const bookingPurchaseData = {
      VirtualPage: propertyCode + '/booking-engine/purchase',
      event: 'Purchase',
      dateTime: new Date(),
      ecommerce: {
        purchase: {
          actionField: {
            transactionId: confirmationNumber,
            id: confirmationNumber,
            hotel_id: propertyCode,
            affiliation: propertyCode,
            revenue: totalCost,
            coupon: promoCode,
            promotionCode: promoCode,
          },
          products: products,
        },
      },
    };

    const purchaseEventData = {
      ...bookingPurchaseData,
      user_properties: user_properties,
    };

    window.dataLayer.push(purchaseEventData);
  }

  if (window.fbq !== undefined) {
    const purchaseRoomData = reservationItems.map((r: ReservationItem) => {
      return {
        id:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        quantity: 1,
        // Custom properties below here:
        name: r.roomType.Name,
        category: 'Room',
        variant: 'From Date: ' + r.DateOfArrival,
        price: r.totalCostForAnalytics,
        taxes: r.totalTaxesForAnalytics,
        netPrice: r.totalCostForAnalytics - r.totalTaxesForAnalytics,
        dateFrom: r.DateOfArrival,
        dateTo: r.DateOfDeparture,
      };
    });
    const purchaseServicesData = reservationItems
      .flatMap((item) => item.Services)
      .map((s: ReservationItemService) => {
        return {
          id: s.Service.ServiceCode,
          quantity: s.Quantity,
          // Custom properties below here:
          name: s.Service.ServiceName,
          category: 'Service',
          price: s.Service.Price,
        };
      });

    const contents = [...purchaseRoomData, ...purchaseServicesData];

    const purchaseData = {
      content_name:
        propertyCode + '/booking-engine/booking-confirmation-Purchase',
      dateTime: new Date(),
      value: totalCost,
      currency: currency,
      contents: contents,
      content_type: 'Room',
      // Custom properties below here:
      transactionId: confirmationNumber,
      transactionAffiliation: propertyCode,
      promotionCode: promoCode,
    };

    window.fbq('track', 'Purchase', purchaseData);
  }

  clearAnalyticsData();
};

const trackSearchAvailabilitySearch = (
  propertyCode: string,
  dateFrom: string,
  dateTo: string,
  numberOfNights: number,
  currency: string,
  bestRate: number,
  ratePlanCode: string,
  promoCode: string,
  rooms: ISearchRoom[],
) => {
  const totalNumberOfAdults = rooms.reduce((a, b) => {
    return a + b.NumberOfAdults;
  }, 0);

  const roomData = rooms.map((r) => {
    return {
      numberOfAdults: r.NumberOfAdults,
      numberOfChildren: r.NumberOfChildren,
      numberOfInfants: r.NumberOfInfants,
    };
  });

  if (typeof window.dataLayer !== 'undefined') {
    window.dataLayer.push({
      VirtualPage: propertyCode + '/booking-engine/SearchAvailability',
      event: 'SearchAvailability',
      dateTime: new Date(),
      currency: currency,
      bestPrice: bestRate,
      dateFrom: dateFrom,
      dateTo: dateTo,
      numberOfNights: numberOfNights,
      ratePlanCode: ratePlanCode,
      promoCode: promoCode,
      numberOfAdults: totalNumberOfAdults,
      rooms: roomData,
    });
  }
};

const trackAddRoomToBasket = (
  propertyCode: string,
  currency: string,
  reservationItem: ReservationItem,
) => {
  if (typeof window.dataLayer !== 'undefined') {
    const products = [reservationItem].map((r) => {
      return {
        id:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        name:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        quantity: 1,
        price: r.totalCost,
        category: 'Room Type: ' + r.roomType.Name,
        brand: 'Rate Plan: ' + r.ratePlan.RatePlanCode,
        variant: 'From Date: ' + r.DateOfArrival,
        metric1: r.DateOfArrival,
        metric2: r.DateOfDeparture,
      };
    });

    window.dataLayer.push({
      VirtualPage: propertyCode + '/booking-engine/addRoomToBasket',
      event: 'AddToCart',
      dateTime: new Date(),
      ecommerce: {
        transactionid: reservationItem.roomId,
        currencyCode: currency,
        add: {
          products: products,
        },
      },
    });
  }

  if (window.fbq !== undefined) {
    let totalValue = 0;

    const addToCartProducts = [reservationItem].map((r) => {
      const roomCost = r.totalCost;
      totalValue += roomCost;

      return {
        id:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        quantity: 1,
        // Custom properties below here:
        name:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        category: 'Room Type: ' + r.roomType.Name,
        brand: 'Rate Plan: ' + r.ratePlan.RatePlanCode,
        price: roomCost,
      };
    });

    const addToCartData = {
      contents: addToCartProducts,
      value: totalValue,
      currency: currency,
    };

    window.fbq('track', 'AddToCart', addToCartData);
  }
};

const trackRemoveRoomFromBasket = (
  propertyCode: string,
  currency: string,
  reservationItem: ReservationItem,
) => {
  if (typeof window.dataLayer !== 'undefined') {
    const products = [reservationItem].map((r) => {
      return {
        id:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        name:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        quantity: 1,
        price: r.totalCost,
        category: 'Room Type: ' + r.roomType.Name,
        brand: 'Rate Plan: ' + r.ratePlan.RatePlanCode,
        variant: 'From Date: ' + r.DateOfArrival,
        metric1: r.DateOfArrival,
        metric2: r.DateOfDeparture,
      };
    });

    window.dataLayer.push({
      VirtualPage: propertyCode + '/booking-engine/RemoveFromCart',
      event: 'RemoveFromCart',
      dateTime: new Date(),
      ecommerce: {
        currencyCode: currency,
        remove: {
          products: products,
        },
      },
    });
  }

  if (window.fbq !== undefined) {
    let totalValue = 0;

    const removeFromCartProducts = [reservationItem].map((r) => {
      const roomCost = r.totalCost;
      totalValue += roomCost;

      return {
        id:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        quantity: 1,
        // Custom properties below here:
        name:
          'Room Type: ' +
          r.roomType.Name +
          '|Rate Plan: ' +
          r.ratePlan.RatePlanCode,
        category: 'Room Type: ' + r.roomType.Name,
        brand: 'Rate Plan: ' + r.ratePlan.RatePlanCode,
        price: roomCost,
      };
    });

    const removeFromCartData = {
      contents: removeFromCartProducts,
      value: totalValue,
      currency: currency,
    };

    window.fbq('track', 'RemoveFromCart', removeFromCartData);
  }
};

const getConsent = (): IConsentRecordInfo => {
  // Check if user has already accepted cookies
  const cookieConsentData = Cookies.get('cookie_consent');
  let cookieInfo: IConsentRecordInfo = {
    date: new Date(),
    analytics: false,
    marketing: false
  };

  try {
    cookieInfo = JSON.parse(atob(cookieConsentData));

    if (cookieInfo && cookieInfo.analytics === undefined) {
      cookieInfo.analytics = true;
    }
    if (cookieInfo && cookieInfo.marketing === undefined) {
      cookieInfo.marketing = true;
    }
  } catch { }

  return cookieInfo
}

const saveConsent = (consent: boolean) => {
  // Use 1 variable, in future maybe we allow users more options.
  const analytics = consent;
  const marketing = consent;

  // Set cookie consent for 1 year
  const val = btoa(
    JSON.stringify({
      analytics: analytics,
      marketing: marketing,
      date: new Date(),
    }),
  );
  Cookies.set('cookie_consent', val, {
    expires: 365,
    path: '/',
  });

  configureGtag();

  if (window.gtag) {
    window.gtag('consent', 'update', {
      ad_storage: marketing ? 'granted' : 'denied',
      ad_user_data: marketing ? 'granted' : 'denied',
      ad_personalization: marketing ? 'granted' : 'denied',
      analytics_storage: analytics ? 'granted' : 'denied',
    });
  }
};

function getCookieInformation(callback: (data: GA4CookieData) => void): void {
  // Use your own ID here ↓
  //const pattern = /_ga_googleTagManagerContainerId=GS\d\.\d\.(.+?)(?:;|$)/;
  const sessionPattern = /_ga_[a-zA-Z0-9]*=GS\d\.\d\.s([0-9]*)\$o/;
  const sessionMatch = document.cookie.match(sessionPattern);
  const sessionParts = sessionMatch?.[1].split('.');

  let clientIdMatch = document.cookie.match('(?:^|;)\\s*_ga=([^;]*)');
  const raw = clientIdMatch ? decodeURIComponent(clientIdMatch[1]) : null;
  if (raw) {
    clientIdMatch = raw.match(/(\d+\.\d+)$/);
  }

  if (!sessionParts || !clientIdMatch) {
    // Cookie not yet available; wait a bit and try again.
    window.setTimeout(() => getCookieInformation(callback), 500);
    return;
  }
  const sessionData = {
    ga_session_id: sessionParts.shift(),
    ga_client_id: clientIdMatch ? clientIdMatch[1] : null,
  };
  callback(sessionData);
}

function clean<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter((value) => {
      return value[1] != null && value[1] != undefined;
    }),
  ) as Partial<T>;
}

function parseQueryParams(): GA4AttributionData {
  const urlParams = new URLSearchParams(window.location.search);

  return {
    utm_id: urlParams.get('utm_id') || undefined,
    utm_source_platform: urlParams.get('utm_source_platform') || undefined,
    utm_source: urlParams.get('utm_source') || undefined,
    utm_medium: urlParams.get('utm_medium') || undefined,
    utm_campaign: urlParams.get('utm_campaign') || undefined,
    utm_term: urlParams.get('utm_term') || undefined,
    utm_content: urlParams.get('utm_content') || undefined,
    // only 1 of these 3 will be provided, but grab what is there.
    gclid: urlParams.get('gclid') || undefined,
    wbraid: urlParams.get('wbraid') || undefined,
    gbraid: urlParams.get('gbraid') || undefined,
    timestamp: Date.now(),
  };
}

const saveGA4AnalyticsData = (googleTagManagerContainerId: string) => {
  const attribution = parseQueryParams();
  attribution.gtmId = googleTagManagerContainerId;
  localStorage.setItem(Attribution_KEY, JSON.stringify(attribution));

  const sessionDataCallback = (data: GA4CookieData) => {
    const currentData = getAnalyticsData() || attribution;
    currentData.ga_client_id = data.ga_client_id;
    currentData.ga_session_id = data.ga_session_id;
    localStorage.setItem(Attribution_KEY, JSON.stringify(currentData));
  };
  getCookieInformation(sessionDataCallback);
};

const getAnalyticsData = (): GA4AttributionData | null => {
  const raw = localStorage.getItem(Attribution_KEY);
  return raw ? JSON.parse(raw) : null;
};

const clearAnalyticsData = () => {
  localStorage.removeItem(Attribution_KEY);
};

function configureGtag() {
  if (!window.dataLayer) {
    window.dataLayer = window.dataLayer || [];
  }
  if (!window.gtag) {
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
  }
}

export default function useAnalyticUtils() {
  return {
    trackPageView: trackPageView,
    trackPromotionView: trackPromotionView,
    trackBookingConfirmation: trackBookingConfirmation,
    trackSearchAvailabilitySearch: trackSearchAvailabilitySearch,
    trackAddRoomToBasket: trackAddRoomToBasket,
    trackRemoveRoomFromBasket: trackRemoveRoomFromBasket,
    saveGA4AnalyticsData: saveGA4AnalyticsData,
    clearAnalyticsData: clearAnalyticsData,
    getAnalyticsData: getAnalyticsData,
    getConsent: getConsent,
    saveConsent: saveConsent,
  };
}
